import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  ButtonWizard,
  TextInput,
  TurnstileCaptcha,
  TurnstileCaptchaRef,
} from '../../components';
import { Link, useNavigate } from 'react-router-dom';
import { KeySquare } from 'lucide-react';
import { useAuth, useNotification } from '../../contexts';
import { extractErrorMessageFromAPI, setChatbotToken } from '../../utils';
import globeImage from '../../assets/images/globe1.png';
import {
  useGetAuthConfigurationQuery,
  useVerifyOtpMutation,
  useSendOtpMutation,
} from '../../store';
import { IAuthenticatedUser } from '../Requisitions/constants/requisition.interface';
import PublicAuthLayout from './PublicAuthLayout';
import softgelLogoImg from '../../assets/images/softgel_logo.png';
import softgelBgImg from '../../assets/images/softgel_bg.png';
import denLogo from '../../assets/images/den_logo_image.svg';
import hathLogo from '../../assets/images/hathway_logo.png';
import { saveAssetToken } from '../../services/knowledgeHub';

export interface ILoginWithOTPForm {
  contact: string;
  otp: string;
  password: string;
  confirmPassword: string;
  user: IAuthenticatedUser;
}

type OTPStep = 'contact' | 'verify';
const RESEND_OTP_DELAY_SECONDS = 60;

export default function LoginWithOTP() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [sendOtp] = useSendOtpMutation();
  const [verifyOtp] = useVerifyOtpMutation();
  const {
    setIsAuthenticated,
    setUser,
    setSessionLimitData,
    setAuthenticationMethod,
  } = useAuth();
  const { data: authConfiguration, isSuccess: isPolicyLoaded } =
    useGetAuthConfigurationQuery();
  const captchaEnabled = Boolean(authConfiguration?.data?.captchaEnabled);
  const captchaRef = useRef<TurnstileCaptchaRef>(null);
  const verifyCaptchaRef = useRef<TurnstileCaptchaRef>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [verifyCaptchaToken, setVerifyCaptchaToken] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<OTPStep>('contact');
  const [contactValue, setContactValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(RESEND_OTP_DELAY_SECONDS);

  const { control, formState, handleSubmit, watch, reset } =
    useForm<ILoginWithOTPForm>({
      defaultValues: {
        contact: '',
        otp: '',
        password: '',
        confirmPassword: '',
      },
    });

  const watchOTP = watch('otp');
  const clientCode = import.meta.env.VITE_ENV_CLIENT_CODE;
  const isSoftgel = clientCode === 'SOFTGEL';
  const logoSrc = isSoftgel
    ? softgelLogoImg
    : clientCode === 'HATHWAY'
      ? hathLogo
      : denLogo;
  const primaryBtnClass = isSoftgel
    ? 'bg-[#9d0b2d] hover:bg-[#7f0823]'
    : 'bg-blue-600 hover:bg-blue-700';
  const primaryTextClass = isSoftgel
    ? 'text-[#9d0b2d] hover:text-[#7f0823]'
    : 'text-blue-600 hover:text-blue-700';

  useEffect(() => {
    if (isPolicyLoaded && authConfiguration?.data?.ssoEnabled) {
      navigate('/signin', { replace: true });
    }
  }, [authConfiguration, isPolicyLoaded, navigate]);

  useEffect(() => {
    let interval: any;
    if (currentStep === 'verify' && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer, currentStep]);

  useEffect(() => {
    if (currentStep === 'verify') {
      reset({
        contact: contactValue,
        otp: '',
        password: '',
        confirmPassword: '',
      });
    }
  }, [currentStep, contactValue, reset]);

  const handleContactSubmit = async (data: ILoginWithOTPForm) => {
    if (!data.contact.trim()) {
      showNotification('error', 'Error', 'Email is required');
      return;
    }
    if (captchaEnabled && !captchaToken) {
      showNotification('error', 'Error', 'Please Complete Security Verification');
      return;
    }

    setIsLoading(true);
    try {
      await sendOtp({
        email: data.contact,
        ...(captchaToken ? { captchaToken } : {}),
      }).unwrap();
      setContactValue(data.contact);
      showNotification(
        'success',
        'OTP Sent',
        `Check your email ${data.contact} for the OTP`,
      );
      setCurrentStep('verify');
      setResendTimer(RESEND_OTP_DELAY_SECONDS);
      setVerifyCaptchaToken(null);

      // Reset form and specifically clear OTP field
      reset({
        contact: data.contact,
        otp: '',
        password: '',
        confirmPassword: '',
      });
    } catch (error) {
      captchaRef.current?.reset();
      setCaptchaToken(null);
      showNotification('error', 'Error', extractErrorMessageFromAPI(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPSubmit = async (data: ILoginWithOTPForm) => {
    if (captchaEnabled && !verifyCaptchaToken) {
      showNotification('error', 'Error', 'Please Complete Security Verification');
      return;
    }

    setIsLoading(true);
    try {
      const response = await verifyOtp({
        email: contactValue,
        otp: data.otp,
        ...(verifyCaptchaToken ? { captchaToken: verifyCaptchaToken } : {}),
      }).unwrap();

      showNotification(
        'success',
        'Login Successful',
        'You have successfully logged in',
      );

      setIsAuthenticated(true);
      setUser(response.data.user);
      setAuthenticationMethod('LEGACY_OTP');
      setChatbotToken(response?.data?.chatbotToken);
      saveAssetToken(response?.data?.assetToken);
      navigate('/');
    } catch (error: any) {
      verifyCaptchaRef.current?.reset();
      setVerifyCaptchaToken(null);

      // Handle device limit reached (same as password login)
      const errorData = error?.data;
      if (errorData?.errorCode === 'SESSION_LIMIT_REACHED') {
        setSessionLimitData({
          activeSessions: errorData.data.activeSessions,
          maxSessions: errorData.data.maxSessions,
          email: contactValue,
        });
        navigate('/device-limit');
        return;
      }
      showNotification('error', 'Error', extractErrorMessageFromAPI(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0 || isLoading) return;
    if (captchaEnabled && !verifyCaptchaToken) {
      showNotification('error', 'Error', 'Please Complete Security Verification');
      return;
    }

    setIsLoading(true);
    try {
      await sendOtp({
        email: contactValue,
        ...(verifyCaptchaToken ? { captchaToken: verifyCaptchaToken } : {}),
      }).unwrap();

      showNotification(
        'success',
        'OTP Sent',
        `Check your email ${contactValue} for the OTP`,
      );
      setResendTimer(RESEND_OTP_DELAY_SECONDS);
      verifyCaptchaRef.current?.reset();
      setVerifyCaptchaToken(null);
    } catch (error) {
      verifyCaptchaRef.current?.reset();
      setVerifyCaptchaToken(null);
      showNotification('error', 'Error', extractErrorMessageFromAPI(error));
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'contact':
        return (
          <div className="space-y-6">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Sign in with OTP
              </h2>
              <p className="text-sm text-gray-600">
                We'll send a 6-digit code to your email
              </p>
            </div>

            <form
              onSubmit={handleSubmit(handleContactSubmit)}
              className="space-y-6"
            >
              <TextInput
                name="contact"
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email',
                  },
                }}
                label="Email Address"
                labelClassName="text-sm font-semibold text-gray-700"
                formState={formState}
                config={{
                  placeholder: 'you@company.com',
                  className: 'h-12 text-base',
                }}
              />

              {captchaEnabled && (
                <TurnstileCaptcha
                  ref={captchaRef}
                  action="send_otp"
                  enabled={captchaEnabled}
                  onVerify={setCaptchaToken}
                  onExpire={() => setCaptchaToken(null)}
                  onError={() => setCaptchaToken(null)}
                  className="my-1"
                />
              )}

              <ButtonWizard
                onBtnClick={handleSubmit(handleContactSubmit)}
                addOnClass={`w-full h-12 ${primaryBtnClass} text-white rounded-xl font-semibold text-base transition-all duration-200 shadow-md hover:shadow-lg`}
                loading={isLoading}
              >
                Send Code
              </ButtonWizard>

              <div className="relative py-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center">
                  <span className="px-4 bg-white text-sm text-gray-500 font-medium">
                    Or
                  </span>
                </div>
              </div>

              <Link
                to="/signin"
                className="w-full h-12 flex items-center justify-center gap-2.5 border-2 border-gray-300 rounded-xl text-base font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
                Sign in with Password
              </Link>
            </form>
          </div>
        );

      case 'verify':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div
                className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 ${
                  isSoftgel ? 'bg-[#f9e7eb]' : 'bg-blue-100'
                }`}
              >
                <KeySquare
                  className={`w-8 h-8 ${
                    isSoftgel ? 'text-[#9d0b2d]' : 'text-blue-600'
                  }`}
                />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Verify your email
              </h2>
              <p className="text-sm text-gray-600">
                Code sent to{' '}
                <span className="font-semibold text-gray-900">
                  {contactValue}
                </span>
              </p>
            </div>

            <form
              onSubmit={handleSubmit(handleOTPSubmit)}
              className="space-y-6"
            >
              <TextInput
                name="otp"
                control={control}
                rules={{
                  required: 'Code is required',
                  pattern: {
                    value: /^\d{6}$/,
                    message: 'Code must be 6 digits',
                  },
                }}
                label="Verification Code"
                labelClassName="text-sm font-semibold text-gray-700"
                formState={formState}
                config={{
                  placeholder: '000000',
                  className:
                    'h-12 text-base text-center tracking-widest text-lg font-semibold',
                  maxLength: 6,
                  inputMode: 'numeric',
                  autoComplete: 'one-time-code',
                  onKeyPress: (e: any) => {
                    if (!/^\d$/.test(e.key)) {
                      e.preventDefault();
                    }
                  },
                }}
              />

              {captchaEnabled && (
                <TurnstileCaptcha
                  ref={verifyCaptchaRef}
                  action="verify_otp"
                  enabled={captchaEnabled}
                  onVerify={setVerifyCaptchaToken}
                  onExpire={() => setVerifyCaptchaToken(null)}
                  onError={() => setVerifyCaptchaToken(null)}
                  className="my-1"
                />
              )}

              <div className="text-center text-sm">
                <span className="text-gray-600">Didn't receive code? </span>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendTimer > 0 || isLoading}
                  className={`font-semibold transition-colors ${
                    resendTimer > 0 || isLoading
                      ? 'text-gray-400 cursor-not-allowed'
                      : `${primaryTextClass} cursor-pointer`
                  }`}
                >
                  Resend
                  {resendTimer > 0 && (
                    <span className="ml-1">
                      ({Math.floor(resendTimer / 60)}:
                      {String(resendTimer % 60).padStart(2, '0')})
                    </span>
                  )}
                </button>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep('contact');
                    setCaptchaToken(null);
                    setVerifyCaptchaToken(null);
                  }}
                  className="flex-1 h-12 bg-white border-2 border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl text-base font-semibold transition-all duration-200"
                >
                  Back
                </button>

                <ButtonWizard
                  onBtnClick={handleSubmit(handleOTPSubmit)}
                  addOnClass={`flex-1 h-12 ${primaryBtnClass} text-white rounded-xl font-semibold text-base transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed`}
                  loading={isLoading}
                  disabled={!watchOTP || watchOTP.length !== 6}
                >
                  Verify
                </ButtonWizard>
              </div>
            </form>
          </div>
        );

      default:
        return null;
    }
  };

  const pageInner = (
    <div className="h-screen overflow-hidden flex font-sans bg-gray-50">
      {/* Left Side - Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-2/5 relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900 h-full">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
        </div>

        {/* Globe Image */}
        <img
          src={globeImage}
          alt="Globe"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-auto opacity-20 pointer-events-none"
          loading="eager"
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 py-8 text-white h-full">
          <div className="space-y-6 max-w-lg">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src={logoSrc}
                alt="Client logo"
                className="w-20 h-auto"
                loading="eager"
              />
            </div>

            {/* Title */}
            <div className="space-y-3">
              <h1 className="text-5xl font-bold leading-[1.1] tracking-tight">
                Enterprise Portal
              </h1>
              <div className="h-1 w-20 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>
              <p className="text-lg text-blue-100 leading-relaxed">
                One-time password authentication
              </p>
            </div>

            {/* Features List */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-3 text-blue-100">
                <div className="w-9 h-9 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/20 shrink-0">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-sm">Passwordless login</span>
              </div>
              <div className="flex items-center gap-3 text-blue-100">
                <div className="w-9 h-9 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/20 shrink-0">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-sm">6-digit verification code</span>
              </div>
              <div className="flex items-center gap-3 text-blue-100">
                <div className="w-9 h-9 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/20 shrink-0">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-sm">Valid for 10 minutes</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - OTP Form */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-10 lg:px-16 py-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
            {renderStepContent()}
          </div>

          <p className="text-center text-xs text-gray-500 mt-8">
            &copy; {new Date().getFullYear()}{' '}
            <Link
              target="_blank"
              to="https://sapphiretech.co.in"
              className="text-gray-600 hover:text-blue-600 transition-colors font-medium"
              rel="noopener noreferrer"
            >
              Sapphire Technocrats Pvt Ltd.
            </Link>
          </p>
        </div>
      </div>
    </div>
  );

  if (isSoftgel) {
    return (
      <div className="h-screen overflow-hidden">
        <PublicAuthLayout
          logoSrc={logoSrc}
          leftBgImageSrc={softgelBgImg}
          accentTextClass="text-teal-500"
        >
          {/* PublicAuthLayout already centers content on right */}
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
              {renderStepContent()}
            </div>
            <p className="text-center text-xs text-gray-500 mt-8">
              &copy; {new Date().getFullYear()}{' '}
              <Link
                target="_blank"
                to="https://sapphiretech.co.in"
                className="text-gray-600 hover:text-blue-600 transition-colors font-medium"
                rel="noopener noreferrer"
              >
                Sapphire Technocrats Pvt Ltd.
              </Link>{' '}
              &mdash; All rights reserved
            </p>
          </div>
        </PublicAuthLayout>
      </div>
    );
  }

  return pageInner;
}
